import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import { alpha } from "@mui/material/styles";

export type TextSkeletonProps = {
  lines?: number;
  maxWidth?: number | string;
};

const pulse = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 0.8; }
  100% { opacity: 0.4; }
`;

const visuallyHiddenStyles = {
  position: "absolute" as const,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden" as const,
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap" as const,
  border: 0,
};

const defaultLineWidths: Array<number | string> = [192, 360, "100%", 330, 300, 360];
const defaultLineHeights: number[] = [10, 8, 8, 8, 8, 8];

const TextSkeleton: React.FC<TextSkeletonProps> = ({ lines = 6, maxWidth }) => {
  const getWidthForIndex = (index: number): number | string => {
    if (index < defaultLineWidths.length) {
      return defaultLineWidths[index];
    }
    return index % 2 === 0 ? "100%" : "75%";
  };

  const getHeightForIndex = (index: number): number => {
    if (index < defaultLineHeights.length) {
      return defaultLineHeights[index];
    }
    return 8;
  };

  return (
    <Box role="status" sx={{ width: "100%", maxWidth: maxWidth ?? "100%" }}>
      <Box display="flex" flexDirection="column">
        {Array.from({ length: lines }, (_, index) => {
          const width = getWidthForIndex(index);
          const height = getHeightForIndex(index);
          const marginBottom = index === 0 ? 4 : index === lines - 1 ? 0 : 2.5;
          return (
            <Box
              key={`text-skeleton-line-${index}`}
              sx={{
                height,
                width,
                borderRadius: 9999,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? alpha(theme.palette.grey[700], 1) : alpha(theme.palette.grey[300], 1),
                animation: `${pulse} 1.8s ease-in-out infinite`,
                mb: marginBottom,
                maxWidth: typeof width === "number" ? `${width}px` : width,
              }}
            />
          );
        })}
      </Box>
      <Box component="span" sx={visuallyHiddenStyles}>
        Loading...
      </Box>
    </Box>
  );
};

export default TextSkeleton;
